import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { Entities } from 'src/app/classes/entities';
import { Entity } from 'src/app/classes/entity.class';
import { EntityUnavailable } from 'src/app/classes/entityunavailable.class';
import { PlanningDayItem } from 'src/app/classes/planning-day-item.class';
import { PlanningDay } from 'src/app/classes/planning-day.class';
import { Planning } from 'src/app/classes/planning.class';
import { PlanningFixed } from 'src/app/classes/planningfixed.class';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { PlanningDetailDialogService } from 'src/app/services/dialog/planning-detail-dialog.service';
import { EntitiesService } from 'src/app/services/entities/entities.service';
import { EntityType, EntityTypeCode } from 'src/app/services/entities/entity-type.class';
import { EntityUnavailableService } from 'src/app/services/entities/entity-unavailable.service';
import { AutocompleteService, AutocompleteType } from 'src/app/services/planning/autocomplete.service';
import { PlanningFixedService } from 'src/app/services/planning/planning-fixed.service';
import { PlanningService } from 'src/app/services/planning/planning.service';
import { UserService } from 'src/app/services/user/user.service';
import { Settings } from 'src/app/settings.class';
import { LocalStorage } from 'src/app/storage.class';
import { Utils } from 'src/app/utils.class';
import { environment } from '../../../environments/environment';
import { PlanningExportDialogComponent } from '../planning-export-dialog/planning-export-dialog.component';
import {
    PLANNING_ROW_HEIGHT,
    PlanningOverviewViewModel,
    TEAM_ROW_HEIGHT,
    USE_ONCE_TEAM_ROW_HEIGHT,
} from './planning-overview-view-model';
import { SetPlanningFixedDialogComponent } from './set-planning-fixed-dialog/set-planning-fixed-dialog.component';

@Component({
    selector: 'app-planning-overview',
    templateUrl: './planning-overview.component.html',
    styleUrls: ['./planning-overview.component.scss'],
    standalone: false,
})
export class PlanningOverviewComponent implements OnInit, OnDestroy {
    @ViewChild('picker') picker: MatDatepicker<Date>;

    public viewModel = new PlanningOverviewViewModel();
    public fromDate: Date;
    public toDate: Date;
    public fixedUntil: Date;
    public fixedDays: PlanningFixed[] = [];
    public primaryWeekStart: Date;
    public primaryWeekEnd: Date;
    public planningDays: PlanningDay[];
    public planningEntities: Entity[];
    entityTypes: EntityType[];
    public print = false;
    teams = [EntityTypeCode.AsfaltTeam];
    environment = environment;
    private dayWith = Settings.DAY_WIDTH;
    EntityTypeCode = EntityTypeCode;
    Settings = Settings;
    private planningSubscriptions = new Subscription();

    entityIdPlanningCount = new Map<number, number>();
    entityHeights = new Map<number, number>();

    mobile = false;

    constructor(
        public dialog: MatDialog,
        private planningDetailDialogService: PlanningDetailDialogService,
        private planningFixedService: PlanningFixedService,
        private entityUnavailableService: EntityUnavailableService,
        private entitiesService: EntitiesService,
        private planningService: PlanningService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private autocompleteService: AutocompleteService,
        private userService: UserService,
        private breakpointObserver: BreakpointObserver,
        private dialogService: DialogService
    ) {}

    @HostListener('window:resize')
    onResize() {
        const numberOfDays = this.calculateNumberOfDays();
        this.setDateRange(this.fromDate, numberOfDays);
    }

    ngOnInit() {
        this.breakpointObserver.observe('(max-width: 722px)').subscribe(match => {
            this.mobile = match.matches;
        });

        combineLatest([this.activatedRoute.url, this.activatedRoute.params]).subscribe(([urlSegments, params]) => {
            let centerDate = new Date();
            const routePath = urlSegments[0]?.path;
            if (routePath === 'print') {
                this.print = true;
                if (params.date) {
                    centerDate = new Date(params.date);
                }
            } else {
                this.print = false;
            }
            if (params.date) {
                centerDate = new Date(params.date);
            }
            this.viewModel.updateViewState();
            this.setCenterDate(centerDate);
            if (params.planningId) {
                this.planningSubscriptions.add(
                    this.planningService.getSingle(+params.planningId).subscribe(planning => {
                        if (this.dialog.openDialogs?.length === 0) {
                            this.openPlanning(planning);
                        }
                    })
                );
            }
        });
    }

    selectedTeamsChange(typeIds: EntityTypeCode[]) {
        // Prevent resubscribe when nothing really changed
        if (JSON.stringify(this.teams) !== JSON.stringify(typeIds)) {
            this.teams = typeIds;
            LocalStorage.PlanningFilter = typeIds;
            this.initPlanningSubscription();
        }
    }

    exportPlanningPdf() {
        this.dialogService.open(PlanningExportDialogComponent);
    }

    setCenterDate(centerDate?: Date) {
        if (!centerDate) {
            centerDate = new Date();
        }
        const numberOfDays = this.calculateNumberOfDays();
        const fromDate = Utils.setTime(centerDate, 0, 0);
        // Mon = 1, Sun = 7
        const dayOfWeek = fromDate.getDay() === 0 ? 7 : fromDate.getDay();
        // maxBack = offset till last monday. ( thue = 2, -1 = mon, sun = 7, -6 = mon )
        const maxBack = dayOfWeek - 1;
        const remainingDaysThisWeek = 8 - dayOfWeek;

        const daysTosubtract = Math.min(maxBack, Math.max(numberOfDays - remainingDaysThisWeek, 0));
        fromDate.setDate(fromDate.getDate() - daysTosubtract);
        this.setDateRange(fromDate, numberOfDays);
    }

    calculateNumberOfDays() {
        return Math.max(1, Math.floor((window.innerWidth - 107) / this.dayWith));
    }

    prev() {
        const numberOfDays = this.calculateNumberOfDays();
        const fromDate = new Date(this.fromDate);
        fromDate.setDate(fromDate.getDate() - Math.min(numberOfDays, 7));
        this.setDateRange(fromDate, numberOfDays);
        this.syncUrl();
    }

    next() {
        const numberOfDays = this.calculateNumberOfDays();
        const fromDate = new Date(this.fromDate);
        fromDate.setDate(fromDate.getDate() + Math.min(numberOfDays, 7));
        this.setDateRange(fromDate, numberOfDays);
        this.syncUrl();
    }

    setDate(date: Date | string | number) {
        if (date) {
            const numberOfDays = this.calculateNumberOfDays();
            const fromDate = Utils.setTime(new Date(date), 0, 0);

            this.setDateRange(fromDate, numberOfDays);
            this.syncUrl();
        }
    }

    // Keeps the URL in sync with the viewed week so a dialog close, refresh or shared link lands on the right week
    private syncUrl() {
        this.router.navigateByUrl(`asfaltplanning/${Utils.formatDate(this.fromDate)}`, { replaceUrl: true });
    }

    openPlanning(planning: Planning) {
        this.planningDetailDialogService
            .openPlanning(planning)
            ?.afterClosed()
            .subscribe(() => {
                this.syncUrl();
            });
    }

    ngOnDestroy(): void {
        this.planningSubscriptions.unsubscribe();
    }

    public mouseEnterDate(day: PlanningDay) {
        this.planningDays.forEach(planningDay => {
            planningDay.fixed = planningDay.date.getTime() <= new Date(day.date).getTime();
        });
    }

    public mouseLeaveDate() {
        this.planningDays.forEach(planningDay => {
            planningDay.fixed =
                planningDay.date.getTime() <= new Date(this.fixedUntil).getTime() ||
                Utils.isSpecificFixedDay(planningDay.date, this.fixedDays);
        });
    }

    public setFixed(day: PlanningDay) {
        if (this.viewModel.lockPlanning) {
            const ref = this.dialog.open(SetPlanningFixedDialogComponent, {
                data: day,
            });
            ref.afterOpened().subscribe(() => {
                this.mouseEnterDate(day);
            });
            const subs = ref.afterClosed().subscribe(() => {
                this.mouseLeaveDate();
                subs.unsubscribe();
            });
        }
    }

    openPicker() {
        this.picker.open();
    }

    onDateSelected(event: any) {
        const date = event.value;
        this.handleDate(date);
    }

    handleDate(date: Date) {
        this.setDate(date);
    }

    private setDateRange(fromDate: Date, numberOfDays: number) {
        const newToDate = new Date(fromDate);
        newToDate.setDate(newToDate.getDate() + numberOfDays);
        if (
            !this.fromDate ||
            this.fromDate.getTime() !== fromDate.getTime() ||
            !this.toDate ||
            this.toDate.getTime() !== newToDate.getTime()
        ) {
            this.fromDate = fromDate;
            this.toDate = newToDate;
            this.calculatePrimaryWeek();
            this.initPlanningSubscription();
        }
    }

    private calculatePrimaryWeek() {
        const weeksCount = {}; // The count of days visible per week
        const weekdates = {}; // First date for each week

        const loopDate = new Date(this.fromDate);
        while (loopDate.getTime() < this.toDate.getTime()) {
            const weeknumber = Utils.getWeek(loopDate);
            if (!weeksCount[weeknumber]) {
                weeksCount[weeknumber] = 0;
                weekdates[weeknumber] = new Date(loopDate);
            }
            weeksCount[weeknumber]++;
            loopDate.setDate(loopDate.getDate() + 1);
        }
        const primaryWeek = Object.keys(weeksCount).sort((b, a) => {
            return weeksCount[a] - weeksCount[b];
        })[0];
        this.primaryWeekStart = weekdates[primaryWeek];

        const showMaxDays = 8 - Utils.dutchDayOfWeek(this.primaryWeekStart);
        const daysToLastVisibleDay = Math.ceil(
            (this.toDate.getTime() - this.primaryWeekStart.getTime()) / (1000 * 60 * 60 * 24)
        );

        this.primaryWeekEnd = Utils.newDate(this.primaryWeekStart);
        this.primaryWeekEnd.setDate(this.primaryWeekEnd.getDate() + Math.min(showMaxDays, daysToLastVisibleDay));
    }

    private initPlanningSubscription() {
        this.planningSubscriptions.unsubscribe();
        this.planningSubscriptions = new Subscription();
        let reInitialize = true;
        const entities$ = this.entitiesService.getMap();
        const requestFromDate = new Date(this.fromDate);
        requestFromDate.setDate(requestFromDate.getDate() - 2);
        const requestToDate = new Date(this.toDate);
        requestToDate.setDate(requestToDate.getDate() + 2);
        const planning$ = this.planningService.getFilteredList(requestFromDate, requestToDate);
        const unavailable$ = this.entityUnavailableService.getFilteredList(requestFromDate, requestToDate);
        const fixed$ = this.planningFixedService.getLatestFixedUntilItem();
        const fixedDays$ = this.planningFixedService.getFixedDays();
        const types$ = this.entitiesService.getTypes();
        let readyTimeout = null;
        this.planningSubscriptions.add(
            combineLatest([entities$, planning$, unavailable$, fixed$, fixedDays$, types$]).subscribe(
                ([entitiesMap, planningList, unavailableList, fixed, fixedDays, types]) => {
                    const entityIdPlanningCount = new Map<number, number>();

                    this.entityTypes = types.data;
                    this.fixedDays = fixedDays;
                    this.fixedUntil = new Date(fixed.fixeduntil);

                    const planningDaysMap = new Map<string, PlanningDay>();
                    const countDate = new Date(this.fromDate);

                    const visibleEntityIds = []
                        .concat(...this.teams)
                        .concat(...this.entityTypes?.filter(p => !!p.visible_overview).map(p => p.key));

                    const userRoleKeys = new Set(this.userService.getCurrent()?.roles?.map(role => role.key) ?? []);
                    const hasMatchingEntityRole = Array.from(entitiesMap.values()).some(entity =>
                        entity.roles?.some(role => userRoleKeys.has(role.key))
                    );

                    const visiblePlanningEntityIds = new Set(
                        planningList.flatMap(planning =>
                            Utils.planningAllEntities(planning)
                                .filter(planningEntity => {
                                    const beginDate = new Date(planningEntity.begindate).getTime();
                                    return (
                                        visibleEntityIds.includes(planningEntity.entitytype_key) &&
                                        beginDate >= this.fromDate.getTime() &&
                                        beginDate < this.toDate.getTime()
                                    );
                                })
                                .map(planningEntity => planningEntity.entity_id)
                        )
                    );

                    this.planningEntities = Array.from(entitiesMap.values())
                        .filter(e => e.entitytypes?.filter(et => visibleEntityIds.includes(et.key)).length > 0)
                        .filter(e => e.enddate === null || new Date(e.enddate).getTime() >= this.fromDate.getTime())
                        .filter(e => !hasMatchingEntityRole || visiblePlanningEntityIds.has(e.id))
                        .sort((a: Entity, b: Entity) => {
                            const aNumber = a.entitytypes[0]?.order ?? a.entitytype_key;
                            const bNumber = b.entitytypes[0]?.order ?? b.entitytype_key;
                            if (aNumber > bNumber) {
                                return 1;
                            }
                            if (aNumber < bNumber) {
                                return -1;
                            }
                            return 0;
                        });

                    const entityEndDateUsed = new Map<Entity, boolean>();

                    while (countDate.getTime() < this.toDate.getTime()) {
                        const planningDay = new PlanningDay();
                        planningDay.date = Utils.setTime(new Date(countDate), 0, 0);
                        planningDay.fixed =
                            planningDay.date.getTime() <= new Date(fixed.fixeduntil).getTime() ||
                            Utils.isSpecificFixedDay(planningDay.date, this.fixedDays);
                        this.planningEntities.forEach(entity => {
                            const planningDayGroup = new Entities();
                            planningDayGroup.entity = entity;
                            if (
                                !entityEndDateUsed.get(entity) &&
                                entity.enddate &&
                                new Date(entity.enddate).getTime() <= countDate.getTime()
                            ) {
                                entityEndDateUsed.set(entity, true);
                                const unav = new EntityUnavailable();
                                unav.begindate = entity.enddate;
                                unav.enddate = this.toDate;
                                unav.entity_id = entity.id;
                                unavailableList.push(unav);
                            }
                            planningDay.entities.push(planningDayGroup);
                        });
                        planningDaysMap.set(Utils.formatDate(countDate), planningDay);
                        countDate.setDate(countDate.getDate() + 1);
                    }

                    unavailableList.forEach(unavailable => {
                        const beginDate = new Date(unavailable.begindate);
                        const firstDateInside = new Date(this.fromDate);
                        while (firstDateInside.getTime() < beginDate.getTime()) {
                            firstDateInside.setDate(firstDateInside.getDate() + 1);
                        }
                        const pdm = planningDaysMap.get(Utils.formatDate(firstDateInside));
                        if (pdm) {
                            const entityGroup = pdm.entities.find(e => e.entity.id === unavailable.entity_id);
                            if (entityGroup) {
                                const duration = new Date(unavailable.enddate).getTime() - firstDateInside.getTime();
                                const currentRangeDuration = this.toDate.getTime() - firstDateInside.getTime();
                                entityGroup.unavailableDays =
                                    duration <= currentRangeDuration
                                        ? Math.round(duration / 86400000)
                                        : Math.round(currentRangeDuration / 86400000);
                            }
                        }
                    });

                    planningList.forEach(planning => {
                        Utils.planningAllEntities(planning).forEach(plnEntity => {
                            plnEntity.entity = entitiesMap.get(plnEntity.entity_id);
                            let count = entityIdPlanningCount.get(plnEntity.entity_id) || 0;
                            entityIdPlanningCount.set(plnEntity.entity_id, count + 1);
                        });

                        const allEntities = Utils.planningAllEntities(planning).filter(e =>
                            visibleEntityIds.includes(e.entitytype_key)
                        );

                        allEntities.forEach(planningEntity => {
                            if (planningEntity) {
                                planningEntity.entity = entitiesMap.get(planningEntity.entity_id);
                                const beginDate = new Date(planningEntity.begindate);
                                const endDate = new Date(planningEntity.enddate);
                                const mapDate = Utils.formatDate(beginDate);
                                const planningDay = planningDaysMap.get(mapDate);
                                if (planningDay) {
                                    const diff = Math.round(
                                        Math.abs((endDate.getTime() - beginDate.getTime()) / 60000)
                                    );
                                    const planningDayItem = new PlanningDayItem();
                                    planningDayItem.planning = planning;
                                    planningDayItem.planningEntity = planningEntity;
                                    planningDayItem.duration = diff;
                                    planningDayItem.minutesOffset = beginDate.getHours() * 60 + beginDate.getMinutes();

                                    const entityGroup = planningDay.entities.find(
                                        group => group.entity.id === planningEntity.entity_id
                                    );

                                    if (entityGroup) {
                                        entityGroup.items.push(planningDayItem);

                                        planningDaysMap.set(mapDate, planningDay);
                                    } else {
                                        console.warn(
                                            'Entitygroup not found, planned outside availability time',
                                            planningEntity.entity_id,
                                            planningEntity.begindate
                                        );
                                    }
                                }
                            }
                        });
                    });

                    const entityHeights = new Map<number, number>();
                    planningDaysMap.forEach(day =>
                        day.entities.forEach(group => {
                            const type = group.entity.entitytypes[0];
                            if (group.entity.use_once && !type?.force_wide) {
                                return;
                            }
                            // row = block + 1px gap. use_once teams sit side by side (1 row), other teams
                            // stack, the rest 2 per row
                            const count = group.items.length;
                            const height = type?.is_team
                                ? group.entity.use_once
                                    ? USE_ONCE_TEAM_ROW_HEIGHT
                                    : Math.max(1, count) * TEAM_ROW_HEIGHT
                                : Math.max(1, Math.ceil(count / 2)) * PLANNING_ROW_HEIGHT;
                            entityHeights.set(
                                group.entity.id,
                                Math.max(entityHeights.get(group.entity.id) ?? 0, height)
                            );
                        })
                    );

                    setTimeout(() => {
                        this.entityIdPlanningCount = entityIdPlanningCount;
                        this.entityHeights = entityHeights;
                    }, 100);

                    if (reInitialize) {
                        this.planningDays = Array.from(planningDaysMap.values());

                        // Preload previous dates to make planning-detail faster. Runs only once,
                        // after the visible week has actually loaded and rendered, so it can't
                        // race with (and short-circuit) planning$'s own request above.
                        const requestFromBufferDate = new Date(this.fromDate);
                        requestFromBufferDate.setDate(requestFromBufferDate.getDate() - 14);
                        this.planningSubscriptions.add(
                            this.planningService.getFilteredList(requestFromBufferDate, requestToDate).subscribe()
                        );
                        this.autocompleteService.getByType(AutocompleteType.contractors).subscribe().unsubscribe();
                    } else {
                        this.planningDays.forEach(pd => {
                            pd.entities = planningDaysMap.get(Utils.formatDate(pd.date)).entities;
                        });
                    }

                    reInitialize = false;

                    if (readyTimeout) {
                        clearTimeout(readyTimeout);
                    }

                    if (planningList?.length) {
                        readyTimeout = setTimeout(() => {
                            setTimeout(() => {
                                document.body.appendChild(document.createElement('readyforpuppeteer'));
                            });
                        }, 2000);
                    }
                }
            )
        );
    }
}
